import React, { useState, useRef } from 'react';
import BottomSheet from '../sheets/BottomSheet';
import RecapTierEditor from './RecapTierEditor';
import RecapTierGroups from './RecapTierGroups';
import './WrapUpSheet.css';

/**
 * Wrap-up — a SHEET, not a place (design §8). The only home of the Recap on
 * phone/iPad. Tally of the session's Song Notes up top, the two family tier
 * editors (Teachers only moved to the Private file on 2026-10-05, pass 4
 * P5.1), Complete + Keep teaching at the bottom.
 *
 * Empty tiers are allowed. Complete asks ONCE when the parent-facing tiers are
 * empty ("Send without a parent recap?") and never blocks — completing flushes
 * the drafts, stops the timer, and closes the lesson.
 *
 * The recap EMAIL sends from here and only from here. Recaps autosave while
 * the teacher types, so any save-triggered send would mail a parent
 * mid-sentence; Complete is the moment the teacher declares the recap
 * finished, and the checkbox keeps the send explicit and declinable.
 */
const WrapUpSheet = ({
	boardCounts,
	timerLabel,
	recap, // useRecapDrafts return
	students = [],
	studentName = '',
	parentName = '',
	emailRecipient = '', // parent email; '' = none on file, hide the toggle
	emailRecipientName = '',
	onSendRecapEmail, // async () => calls sendLessonNotes with family-visible tiers
	onComplete, // async () => navigates away
	onClose,
	// Pass 3 A4: opened from the Recap drawer, Keep teaching goes back to the
	// drawer and says so; from anywhere else it returns to the lesson. The
	// page owns the "where from" (one overlay state), this sheet only renders
	// what it is handed. Close / Esc / scrim still dismiss everything.
	onKeepTeaching = null,
	backToRecap = false,
	warmupDraft = null, // useWarmupRoutine return (plan branch 2): dirty/changes/save/discard
}) => {
	const [confirming, setConfirming] = useState(false);
	const [completing, setCompleting] = useState(false);
	const [completeError, setCompleteError] = useState('');
	const [emailRecap, setEmailRecap] = useState(true);
	// A sent email stays sent: if the email lands but completing the lesson
	// fails, the retry must not mail the family twice.
	const emailSentRef = useRef(false);

	const stillYours = boardCounts.captured;
	const tally = [
		`${boardCounts.landed} landed`,
		`${boardCounts.focus} focus`,
		`${stillYours} still yours`,
	].join(' · ');

	// The family sees the everyone + parents tiers; if both are empty, one
	// confirm before sending a recap with nothing for the parents.
	const parentFacingEmpty =
		!recap.tierHasContent('student') && !recap.tierHasContent('parent');

	const canEmail = Boolean(emailRecipient && onSendRecapEmail);
	const willEmail = canEmail && emailRecap && !parentFacingEmpty;

	const runComplete = async () => {
		setCompleting(true);
		setCompleteError('');
		await recap.flushAll();
		// Email BEFORE completing: on success onComplete navigates away, which
		// is too late to tell the teacher a send failed. A failed send re-enables
		// the sheet instead of completing — unchecking the box is the escape
		// hatch when email is down and the lesson still has to close.
		if (willEmail && !emailSentRef.current) {
			try {
				await onSendRecapEmail();
				emailSentRef.current = true;
			} catch (error) {
				console.error('Error sending recap email:', error);
				setCompleting(false);
				setCompleteError(
					'The recap email didn’t send. Try again, or uncheck the email box to complete without it.'
				);
				return;
			}
		}
		const ok = await onComplete();
		// On success onComplete navigates away — leave the button disabled through
		// the hand-off. On failure the write didn't land: re-enable and say so, the
		// drafts are already flushed and safe.
		if (!ok) {
			setCompleting(false);
			setCompleteError('Could not complete the lesson. Your notes are saved — try again.');
		}
	};

	// An unsaved warmup routine draft asks ONCE, before the recap confirm
	// (plan branch 2; Anthony: "an option to save the updated routine or
	// discard before they are done"). Save writes the student's document and
	// completes; Discard drops the draft and completes; Keep teaching closes.
	const routineDirty = Boolean(warmupDraft && warmupDraft.dirty);
	const [routineAsked, setRoutineAsked] = useState(false);

	const continueAfterRoutine = () => {
		if (parentFacingEmpty && !confirming) {
			setConfirming(true);
			return;
		}
		runComplete();
	};

	const handleCompleteClick = () => {
		if (routineDirty && !routineAsked) {
			setRoutineAsked(true);
			return;
		}
		continueAfterRoutine();
	};

	const saveRoutineAndComplete = async () => {
		const ok = await warmupDraft.save();
		if (!ok) {
			setCompleteError('The warmup routine did not save. Try again, or discard it to complete without it.');
			return;
		}
		continueAfterRoutine();
	};

	const discardRoutineAndComplete = () => {
		warmupDraft.discard();
		continueAfterRoutine();
	};

	return (
		<BottomSheet
			title="Wrap up"
			titleId="ld-wrapup-title"
			onClose={onClose}
			className="ld-wrapup"
			header={
				timerLabel ? (
					<span className="ld-wrapup__timer">
						<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
						{timerLabel}
					</span>
				) : null
			}
			footer={
				<>
					{canEmail && !parentFacingEmpty && (
						<label className="ld-wrapup__email" data-guide="wrapup-email">
							<input
								type="checkbox"
								checked={emailRecap}
								disabled={completing}
								onChange={(e) => setEmailRecap(e.target.checked)}
							/>
							<span>
								Email this recap to{' '}
								<strong>{emailRecipientName || emailRecipient}</strong>
							</span>
						</label>
					)}
					{!canEmail && !recap.noStudentLinked && !parentFacingEmpty && (
						<p className="ld-wrapup__email-note">
							No parent email on file — the recap won’t be emailed.
						</p>
					)}
					{routineAsked && routineDirty && (
						<div className="ld-wrapup__routine" role="alert">
							<p className="ld-wrapup__confirm">
								The warmup routine has unsaved changes
								{warmupDraft.changes && warmupDraft.changes.length ? ` — ${warmupDraft.changes.join('; ')}` : ''}.
							</p>
							<div className="ld-wrapup__routine-actions">
								<button type="button" className="ld-wrapup__routine-btn ld-wrapup__routine-btn--save" disabled={completing || warmupDraft.saving} onClick={saveRoutineAndComplete}>
									Save routine &amp; complete
								</button>
								<button type="button" className="ld-wrapup__routine-btn" disabled={completing || warmupDraft.saving} onClick={discardRoutineAndComplete}>
									Discard &amp; complete
								</button>
							</div>
						</div>
					)}
					{confirming && (
						<p className="ld-wrapup__confirm">
							Send without a parent recap? The family will see the Everyone tier only.
						</p>
					)}
					{completeError && (
						<p className="ld-wrapup__error" role="alert">
							{completeError}
						</p>
					)}
					<button
						type="button"
						className="ld-wrapup__complete"
						disabled={completing}
						data-guide="wrapup-complete"
						onClick={handleCompleteClick}>
						<i className="fa-sharp fa-solid fa-flag-checkered" aria-hidden="true" />
						{routineAsked && routineDirty ? 'Complete without saving' : confirming ? 'Complete anyway' : 'Complete lesson'}
					</button>
					<button type="button" className="ld-wrapup__keep" onClick={onKeepTeaching || onClose}>
						{backToRecap ? 'Keep teaching · back to the Recap' : 'Keep teaching'}
					</button>
				</>
			}>
			<div className="ld-wrapup__tally">
				<div className="ld-wrapup__chips">
					<span className="ld-wrapup__chip ld-wrapup__chip--landed">
						<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
						Landed
					</span>
					<span className="ld-wrapup__chip ld-wrapup__chip--focus">
						<i className="fa-sharp fa-solid fa-bullseye" aria-hidden="true" />
						Focus
					</span>
				</div>
				<span className="ld-wrapup__count">{tally}</span>
			</div>

			<div className="ld-wrapup__tiers">
				<RecapTierGroups
					renderTier={(tier) => (
						<RecapTierEditor
							key={tier.key}
							tier={tier}
							// LIVE content, not `tiers[].content` — this sheet mounts a
							// SECOND editor per tier over the right column's, and the state
							// copy is stale the moment anyone types (see useRecapDrafts →
							// liveTierContent). Reading the stale copy rendered empty boxes
							// and made Complete lesson look like it discarded the recap.
							content={recap.liveTierContent(tier.visibility)}
							onUpdate={(payload) => recap.handleTierUpdate(tier.visibility, payload)}
							students={students}
							studentName={studentName}
							parentName={parentName}
							unlinked={recap.noStudentLinked}
						/>
					)}
				/>
			</div>
		</BottomSheet>
	);
};

export default WrapUpSheet;
